import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';

/**
 * Golden-path view: finding → remediations → defense signals → related runs (Phase A).
 */
export function FindingDetailPage() {
  const { findingId } = useParams();
  const { context } = usePilotContext();
  const customerId = context?.customer_id || 'default';
  const [lineage, setLineage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!findingId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(
          `/api/golden-path/${encodeURIComponent(customerId)}/findings/${encodeURIComponent(findingId)}/lineage`,
          { timeoutMs: 25000 }
        );
        if (!mounted) return;
        setLineage(res);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [customerId, findingId]);

  const f = lineage?.finding;
  const remediations = lineage?.remediations || [];
  const signals = lineage?.defense_signals || [];
  const runIds = lineage?.related_run_ids || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Finding lineage</h1>
          <p className="text-muted-foreground">
            Golden path: finding, remediations, defense signals, and related runs.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/graph-findings">← Graph findings</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-mono text-sm">{findingId}</CardTitle>
          <CardDescription>
            {f?.source === 'neo4j' ? 'Loaded from graph store.' : 'Graph detail optional when Neo4j has this Finding id.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {loading && <p className="text-muted-foreground">Loading lineage…</p>}
          {error && <p className="text-red-500">{error}</p>}
          {!loading && !error && f && (
            <>
              <div>
                <span className="font-medium text-foreground">Title:</span> {f.title || '—'}
              </div>
              <div>
                <span className="font-medium text-foreground">Severity:</span>{' '}
                <Badge variant="outline">{f.severity || '—'}</Badge>
              </div>
            </>
          )}
          {!loading && !error && !f && (
            <p className="text-muted-foreground">
              No Neo4j Finding row for this id (lineage still works for remediations and signals you attach).
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Remediations</CardTitle>
          <CardDescription>
            Persisted under golden path. Clear requires a passing retest via API.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {remediations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No remediation records yet for this finding.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {remediations.map((r) => (
                <li key={r.remediation_id} className="rounded-md border border-border/60 p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="rounded bg-secondary px-1 text-xs">{r.remediation_id}</code>
                    <Badge variant="outline">{r.status}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    retest: {r.retest_run_id || '—'} | passed: {String(r.retest_passed)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Defense signals</CardTitle>
          <CardDescription>Fusion triage records with matching finding_id.</CardDescription>
        </CardHeader>
        <CardContent>
          {signals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No defense signals for this finding yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {signals.map((s) => (
                <li key={s.defense_signal_id} className="rounded-md border border-border/60 p-2">
                  <code className="rounded bg-secondary px-1 text-xs">{s.defense_signal_id}</code>
                  <span className="ml-2 text-muted-foreground">{s.action}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Related runs</CardTitle>
          <CardDescription>Run ids referenced by remediations or signals.</CardDescription>
        </CardHeader>
        <CardContent>
          {runIds.length === 0 ? (
            <p className="text-sm text-muted-foreground">None linked yet.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {runIds.map((rid) => (
                <li key={rid}>
                  <Link
                    className="font-mono text-sm text-primary underline"
                    to={`/runs/${encodeURIComponent(rid)}`}
                  >
                    {rid}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
